import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './messages/en.json';
import de from './messages/de.json';
import fr from './messages/fr.json';
import es from './messages/es.json';
import it from './messages/it.json';
import zh from './messages/zh.json';
import zhTw from './messages/zh-tw.json';
import ru from './messages/ru.json';
import ms from './messages/ms.json';
import id from './messages/id.json';
import ja from './messages/ja.json';
import ko from './messages/ko.json';
import { DEFAULT_LOCALE, LOCALE_CODES } from './config';

const LOCALE_COOKIE = 'locale';
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

/**
 * Determine the initial language synchronously from the URL, before the
 * first React render. This avoids a flash of English content on a locale
 * route (e.g. /de/pro) while the app is a client-rendered SPA rather than
 * a hydrated SSR page.
 */
function detectInitialLocale(): string {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const segment = window.location.pathname.split('/')[1];
  return LOCALE_CODES.includes(segment) ? segment : DEFAULT_LOCALE;
}

function hasLocaleCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=`).test(document.cookie);
}

/**
 * middleware.ts does the actual geo-redirect (edge-side, before any HTML
 * ships), but it only ever issues a redirect - it never has to inject a
 * "no redirect needed" cookie on a pass-through response. This is where
 * that gets recorded instead: the first time the app boots with no cookie
 * yet, remember whatever locale it landed on (a geo redirect, a shared
 * /de/... link, or plain English) so middleware doesn't recompute it on
 * every subsequent visit.
 */
function persistLocaleCookie(locale: string): void {
  if (typeof document === 'undefined' || hasLocaleCookie()) return;
  document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

const initialLocale = detectInitialLocale();
persistLocaleCookie(initialLocale);

// All catalogs are bundled up front (no lazy-loading/backend plugin), so
// changeLanguage() is synchronous in practice and useSuspense can stay off -
// there's never a network round-trip or missing-resource flash.
i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    de: { translation: de },
    fr: { translation: fr },
    es: { translation: es },
    it: { translation: it },
    zh: { translation: zh },
    // i18next capitalizes the region subtag when building its resolution
    // hierarchy for a code like "zh-tw" (it resolves via "zh-TW", not
    // "zh-tw"), so the resource key must match that casing even though the
    // URL segment / `lng` value passed around the app stays lowercase.
    'zh-TW': { translation: zhTw },
    ru: { translation: ru },
    ms: { translation: ms },
    id: { translation: id },
    ja: { translation: ja },
    ko: { translation: ko },
  },
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

export default i18n;
